<script setup>
import router from '@/router/index.js'
import SmoothScroll from 'smooth-scroll/dist/smooth-scroll.min.js'
import {
  computed,
  defineAsyncComponent,
  onBeforeMount,
  onMounted,
  ref,
  watch
} from 'vue'
import { useRoute } from 'vue-router'

import IconSetHeart from '@/components/basic/atoms/IconSetHeart.vue'
import IconSetWeb from '@/components/basic/atoms/IconSetWeb.vue'
import Overlay from '@/components/basic/atoms/Overlay.vue'
import { useGlobals } from '@/composables/useGlobals.js'
import SiteConfig from '@/services/SiteConfig.js'
import { useCmsBlocks } from '@/stores/cmsBlocks.js'
import { useCmsVariables } from '@/stores/cmsVariables.js'
import { useCustomerStore } from '@/stores/customer.js'
import { useFeaturesStore } from '@/stores/features.js'
import { useHead } from '@unhead/vue'
import { storeToRefs } from 'pinia'
import { useDisplay, useTheme } from 'vuetify'
import TheAuthentication from './components/basic/organisms/TheAuthentication.vue'
import { useAccessibilityStore } from './stores/accessibility.js'
import { useCartStore } from './stores/cart.js'
import { useConsentStore } from './stores/consent.js'

const ThePopoverWrapper = defineAsyncComponent(
  () => import('@/components/basic/molecules/ThePopoverWrapper.vue')
)
const TheCountrySwitcher = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheCountrySwitcher.vue')
)
const AlertBanner = defineAsyncComponent(
  () => import('@/components/basic/atoms/AlertBanner.vue')
)
const TheDialog = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheDialog.vue')
)
const AccessibilityButton = defineAsyncComponent(
  () => import('@/components/basic/atoms/AccessibilityButton.vue')
)
const CustomerTools = defineAsyncComponent(
  () => import('@/components/basic/atoms/CustomerTools.vue')
)
const getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [{ default: CouponHelper }] = await Promise.all([
      await import('@/services/helpers/CouponHelper')
    ])
    cache = { CouponHelper }
    return cache
  }
})()

const { translate } = useGlobals()
const customer = useCustomerStore()
const blocks = useCmsBlocks()
const variables = useCmsVariables()
const features = useFeaturesStore()
const display = useDisplay()
const theme = useTheme()
const accessibility = useAccessibilityStore()

const consent = useConsentStore()
consent.initializeConsentListeners()

const HIGH_CONTRAST_TOGGLE = 'show_high_contrast'
const themeName = localStorage.getItem(HIGH_CONTRAST_TOGGLE)
  ? 'vicampoThemeHighContrast'
  : 'vicampoThemeRebrand'

if (typeof theme.change === 'function') {
  theme.change(themeName)
} else {
  theme.global.name.value = themeName
}

const { customerProfile, isLoginShowing, showAdminPermissionError } =
  storeToRefs(customer)

const route = useRoute()

const countrySwitcherData = ref(null)

const isLoggedInAsCustomer = ref(false)

// Function to check cookie and update reactive value
const checkCookie = async () => {
  if (typeof document !== 'undefined') {
    const { default: CookieHelper } = await import(
      '@/services/helpers/CookieHelper'
    )
    const cookieValue = CookieHelper.getCookie('original-customer-id')
    isLoggedInAsCustomer.value = !!cookieValue
  } else {
    isLoggedInAsCustomer.value = false
  }
}

// Check on mount and when route changes
onMounted(async () => {
  await checkCookie()
})

// Watch for route changes to check cookie
watch(
  () => route.path,
  async () => {
    await checkCookie()
  }
)

const alertBannerText = computed(() => {
  if (showAdminPermissionError.value) {
    return translate('fe_app_admin_permission_required')
  }
  if (isLoggedInAsCustomer.value) {
    return translate('fe_app_logged_in_as', customerProfile.value?.email)
  }

  return ''
})

const countrySwitcherTitle = computed(() => {
  const switchTo = translate(
    'fe_app_country_' + countrySwitcherData.value?.siteId
  )
  return translate('fe_app_country_switcher_header', switchTo)
})

const displayClassUp = computed(() => {
  const width = display.width.value
  const thresholds = display.thresholds.value

  switch (true) {
    case width >= thresholds.xxl:
      return 'display-xxl-and-up display-xl-and-up display-lg-and-up display-md-and-up display-sm-and-up display-xs-and-up'
    case width >= thresholds.xl:
      return 'display-xl-and-up display-lg-and-up display-md-and-up display-sm-and-up display-xs-and-up'
    case width >= thresholds.lg:
      return 'display-lg-and-up display-md-and-up display-sm-and-up display-xs-and-up'
    case width >= thresholds.md:
      return 'display-md-and-up display-sm-and-up display-xs-and-up'
    case width >= thresholds.sm:
      return 'display-sm-and-up display-xs-and-up'
    case width >= thresholds.xs:
      return 'display-xs-and-up'
    default:
      return ''
  }
})

const updateShowLogin = (visible) => {
  customer.showLogin(visible)
}

onBeforeMount(async () => {
  await customer.establishSession()

  void variables.fetchDefaults()
  void blocks.fetchDefaults()
  void features.refresh()

  const [{ default: TagManager }, { default: SiteConfig }] = await Promise.all([
    await import('@/services/analytics/TagManager'),
    await import('@/services/SiteConfig')
  ])

  TagManager.pushToDataLayer({ application: 'fe-app' })
  countrySwitcherData.value = await SiteConfig.offerOtherSite()

  // refresh session as soon as the tab becomes active again
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    customer.establishSession()
  })
})

onMounted(async () => {
  addTrustedShopsBadge()

  accessibility.updateCSSVariables()

  SmoothScroll('a[href*="#"]', {
    speed: 500,
    speedAsDuration: true,
    header: '.navbar > div',
    easing: 'easeInOutCubic',
    updateURL: false
  })
})

const bundleSvgUrl = new URL('@/assets/images/bundle.svg', import.meta.url).href
const bottleSvgUrl = new URL('@/assets/images/bottle.svg', import.meta.url).href

useHead({
  titleTemplate: `${SiteConfig.devMode ? '[ 👾] ' : ''}%s | ${SiteConfig.name} `,
  link: [
    {
      rel: 'preload',
      href: bundleSvgUrl,
      as: 'image',
      type: 'image/svg+xml',
      fetchpriority: 'high'
    },
    {
      rel: 'preload',
      href: bottleSvgUrl,
      as: 'image',
      type: 'image/svg+xml',
      fetchpriority: 'high'
    }
  ]
})

const addTrustedShopsBadge = () => {
  const siteIdToTsIdMap = {
    de: import.meta.env.VITE_TRUSTED_SHOPS_ID_VIC_DE,
    at: import.meta.env.VITE_TRUSTED_SHOPS_ID_VIC_AT,
    ch: import.meta.env.VITE_TRUSTED_SHOPS_ID_VIC_CH
  }

  const locale = SiteConfig.locale
  const tsId = siteIdToTsIdMap[locale]

  if (!tsId) return

  const tsScript = document.createElement('script')
  tsScript.async = true
  tsScript.src = `//widgets.trustedshops.com/js/${tsId}.js`

  tsScript.setAttribute('data-desktop-y-offset', '0')
  tsScript.setAttribute('data-mobile-y-offset', '0')
  tsScript.setAttribute('data-desktop-disable-reviews', 'false')
  tsScript.setAttribute('data-desktop-enable-custom', 'false')
  tsScript.setAttribute('data-desktop-position', 'left')
  tsScript.setAttribute('data-desktop-custom-width', '156')
  tsScript.setAttribute('data-desktop-enable-fadeout', 'true')
  tsScript.setAttribute('data-disable-mobile', 'false')
  tsScript.setAttribute('data-disable-trustbadge', 'false')
  tsScript.setAttribute('data-mobile-custom-width', '156')
  tsScript.setAttribute('data-mobile-disable-reviews', 'false')
  tsScript.setAttribute('data-mobile-enable-custom', 'false')
  tsScript.setAttribute('data-mobile-position', 'left')
  tsScript.setAttribute('data-mobile-enable-topbar', 'false')
  tsScript.setAttribute('data-mobile-enable-fadeout', 'true')
  tsScript.setAttribute('data-color-scheme', 'dark')

  document.body.appendChild(tsScript)
  toggleTrustBadgeVisibility()
}

const toggleTrustBadgeVisibility = () => {
  const trustBadge = document.querySelector('[id^="trustbadge-container-"]')
  if (!trustBadge) return

  const { pathname } = window.location
  const shouldHide = /checkout\/v2|pdp|cms|cat/.test(pathname)

  trustBadge.classList.toggle('d-none', shouldHide)
}

router.afterEach(async () => {
  toggleTrustBadgeVisibility()

  const { CouponHelper } = await getModules()
  const cart = useCartStore()

  const { storedCouponCode } = storeToRefs(cart)

  const coupon = await CouponHelper.checkForCoupon()
  if (coupon !== null && coupon !== storedCouponCode) {
    await cart.applyCouponCode(coupon)
  } else {
    await cart.refreshCart()
  }
})
</script>
<template>
  <VApp id="root" :class="displayClassUp">
    <AlertBanner
      v-if="isLoggedInAsCustomer || showAdminPermissionError"
      :text="alertBannerText"
    />
    <RouterView />
    <Overlay />
    <IconSetHeart />
    <IconSetWeb />
    <TheDialog
      v-if="countrySwitcherData"
      :title="countrySwitcherTitle"
      v-model="countrySwitcherData"
      @close="countrySwitcherData = null"
    >
      <TheCountrySwitcher
        :country-switcher-data="countrySwitcherData"
        @close="countrySwitcherData = null"
      />
    </TheDialog>
    <TheDialog v-model="isLoginShowing" @close="updateShowLogin(false)">
      <TheAuthentication
        :allowed-actions="['login', 'forgot-password', 'two-factor']"
        :message="translate('disclaimer_you_own_already_an_account')"
        :message-title="
          translate('disclaimer_you_own_already_an_account_title')
        "
        @success="updateShowLogin(false)"
      />
    </TheDialog>
    <ThePopoverWrapper :show-nps="false" />
    <CustomerTools>
      <AccessibilityButton />
    </CustomerTools>
  </VApp>
</template>
